import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { PerformanceTaskResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfLayout } from "../pdf/PdfLayout";
import MarkdownRenderer from "../custom-markdown";
import { useTranslation } from "react-i18next";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

type PDFProps = {
  imageUrl?: string;
  customizationSettings?: PdfCustomizationSettings;
} & Required<
  Pick<
    PerformanceTaskResponseDto,
    | "courseName"
    | "gradeLevel"
    | "prompt"
    | "performanceCriteria"
    | "description"
    | "studentInstructions"
    | "materialsNeeded"
    | "createdAt"
    | "authorName"
  >
>;

const styles = StyleSheet.create({
  section: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f8fafc", // slate-50
    borderRadius: 8,
    background: "linear-gradient(to bottom right, #f8fafc, #eff6ff)", // from-gray-50 to-blue-50
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#1e293b", // slate-800
    marginBottom: 8,
    fontFamily: "Montserrat",
  },
  content: {
    fontSize: 10,
    color: "#475569", // slate-600
    lineHeight: 1.4,
    fontFamily: "Montserrat",
  },
  list: {
    marginLeft: 12,
  },
  listItem: {
    flexDirection: "row",
    marginBottom: 4,
  },
  bullet: {
    width: 10,
    fontSize: 10,
    color: "#3b82f6", // blue-500
    fontFamily: "Montserrat",
  },
  criteriaList: {
    marginLeft: 12,
  },
  criteriaItem: {
    fontSize: 10,
    color: "#475569", // slate-600
    marginBottom: 2,
    fontFamily: "Montserrat",
  },
  markdownContainer: {
    marginBottom: 8,
  },
});

const PerformanceTaskDocument = ({
  performanceCriteria,
  imageUrl,
  courseName,
  gradeLevel,
  prompt,
  description,
  studentInstructions,
  materialsNeeded,
  createdAt,
  authorName,
  customizationSettings,
}: PDFProps) => {
  const numericGradeLevel =
    typeof gradeLevel === "string" ? parseInt(gradeLevel, 10) : gradeLevel;
  const { t } = useTranslation();
  const markdownStyle = {
    text: {
      text: {
        ...styles.content,
        fontSize: scaleFontSize(
          styles.content.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
    listItem: {
      text: {
        ...styles.content,
        fontSize: scaleFontSize(
          styles.content.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  const sectionTitleStyle = [
    styles.sectionTitle,
    {
      fontSize: scaleFontSize(
        styles.sectionTitle.fontSize,
        customizationSettings?.fontSize,
      ),
    },
  ];
  return (
    <PdfLayout
      courseName={courseName}
      gradeLevel={numericGradeLevel}
      topic={prompt}
      customizationSettings={customizationSettings}
      imageUrl={imageUrl}
      createdDate={createdAt}
      author={authorName}
      headerTextColor="#3b82f6"
    >
      {description && (
        <View style={styles.section}>
          <Text style={sectionTitleStyle}>
            {t("performanceTasks.description")}
          </Text>
          <MarkdownRenderer customStyle={markdownStyle} content={description} />
        </View>
      )}

      {studentInstructions && (
        <View style={styles.section} wrap={false}>
          <Text style={sectionTitleStyle}>
            {t("performanceTasks.studentInstructions")}
          </Text>
          <MarkdownRenderer
            customStyle={markdownStyle}
            content={studentInstructions}
          />
        </View>
      )}

      <View style={styles.section} wrap={false}>
        <Text style={sectionTitleStyle}>
          {t("performanceTasks.performanceCriteria")}
        </Text>
        <View style={styles.criteriaList}>
          {performanceCriteria.map((criteria, index) => (
            <View key={index} style={styles.listItem}>
              <Text style={styles.bullet}>•</Text>
              <MarkdownRenderer
                customStyle={markdownStyle}
                content={criteria}
              />
            </View>
          ))}
        </View>
      </View>

      {materialsNeeded && materialsNeeded.length > 0 && (
        <View style={styles.section} wrap={false}>
          <Text style={sectionTitleStyle}>
            {t("performanceTasks.materialsNeeded")}
          </Text>
          <View style={styles.list}>
            {materialsNeeded.map((material, index) => (
              <View key={index} style={styles.listItem}>
                <Text style={styles.bullet}>•</Text>
                <MarkdownRenderer
                  customStyle={markdownStyle}
                  content={material}
                />
              </View>
            ))}
          </View>
        </View>
      )}
    </PdfLayout>
  );
};

function PerformanceTaskCardPDF(props: PDFProps) {
  return <PerformanceTaskDocument {...props} />;
}

export default PerformanceTaskCardPDF;
